import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";

import IconView from "@/shared/components/IconView/IconView";
import IconButton from "@/shared/components/IconButton/IconButton";
import Logotype from "@/components/Logotype/Logotype";
import Container from "@/components/Container/Container";
import Backdrop from "@/shared/components/Backdrop/Backdrop";
import LinkButton from "@/components/Button/LinkButton";
import HeaderForm from "@/components/Header/HeaderForm";
import { PlayControl } from "@/components/Player/PlayerControls";
import PanelHeaderMenu from "@/components/Header/PanelHeaderMenu";
import Portal from "@/shared/components/Portal/Portal";

import EgwWebFont from "src/assets/EgwWebFont";
import { removeBR } from "@/shared/utils/content/content";
import { systemActions } from "@/store/actions";
import { useFullScreen } from "@/hooks/index";
import { NEXT_URLS } from "@/utils/routes/routes.constants";
import { selectorSearchParams } from "@/shared/store/search/selectors";
import useMediaQuery, { MEDIA_QUERIES } from "@/shared/hooks/useMediaQuery";
import { goToFullAppLabel, MenuItemsList } from "@/utils/MenuItems.constants";
import { MENU_ITEM_LOCATION } from "@/shared/utils/menu";
import { useAuthContext } from "@/contexts/auth";
import { URLS } from "@/shared/utils/url/url";
import { getImageByClass } from "../../../assets/CategoryIcons";
import { useTheme } from "@/hooks/useTheme";
import { useSearchContext } from "@/contexts/search";
import { EL_ID_PORTAL_MAIN_AFTER } from "@/shared/components/Portal/PortalMainAfter/constants";
import ThreeDots from "@/components/ThreeDots";
import useIsDOMHydratedAfterSSR from "@/hooks/useIsDOMHydratedAfterSSR";

const ButtonTheme = dynamic(() => import("@/components/DropDown/ButtonTheme"), {
  ssr: false
});

const SearchExampleView = dynamic(() => import("@/components/Header/SearchExampleView"), {
  ssr: false
});

import styles from "./Header.module.scss";

const Header = ({ fullVersionLink }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();
  const [isShowMobileForm, setShowMobileForm] = useState(false);
  const [showSearchHelp, setShowSearchExamples] = useState(false);
  const {
    isLogin,
    login,
    logout
  } = useAuthContext();
  const { isDarkTheme } = useTheme();
  const showSideMenu = useSelector((state) => state.system.showSideMenu);
  const showAdvSearchPanel = useSelector((state) => state.system.showAdvSearchPanel);
  const searchParams = useSelector(selectorSearchParams);
  const {
    fullScreen,
    toggleFullScreen
  } = useFullScreen();
  const { search } = useSearchContext();

  const isHydrated = useIsDOMHydratedAfterSSR();
  const isMobile = useMediaQuery(MEDIA_QUERIES.untilTablet);
  const isMobileAndHydrated = isMobile && isHydrated;

  const isReader = router.asPath.includes(NEXT_URLS.read);

  const toggleMenu = () => {
    dispatch(systemActions.setShowSideMenu(!showSideMenu));
  };

  const onClickAuth = () => {
    if (isLogin) {
      logout();
    } else {
      login();
    }
  };

  const closeSearchHelp = () => {
    setShowSearchExamples(false);
  };

  const hideMobileForm = () => {
    setShowMobileForm(false);
  };

  const toggleSearchHelp = () => {
    setShowSearchExamples(!showSearchHelp);
  };

  const MenuItemsFinal = [];

  for (const item of MenuItemsList) {
    const {
      locations,
      label,
      id
    } = item;

    const isLocationSuitable = locations.includes(MENU_ITEM_LOCATION.MENU);

    const isAuthValid = (!item.onlyAuth || isLogin) && (!item.onlyNoAuth || !isLogin);

    const isItemGoToFullApp = label === goToFullAppLabel;

    if (isLocationSuitable && isAuthValid && (!isItemGoToFullApp || isMobileAndHydrated)) {
      const hrefFinal = isItemGoToFullApp ? fullVersionLink : item.url;

      MenuItemsFinal.push({
        ...item,
        href: hrefFinal,
        label: removeBR(t(label)),
        icon: item.icon || getImageByClass(item.add_class, isDarkTheme, id),
        onClick: (id === URLS.login || id === URLS.logout) ? onClickAuth : undefined,
      });
    }
  }

  const elemThemeDropDown = (
    <ButtonTheme className={styles["dd-theme"]} onClick={closeSearchHelp}/>
  );

  const btnMenu = (
    <IconButton
      icon={EgwWebFont["burger-menu"]}
      active={showSideMenu}
      className={styles["menu-button"]}
      onClick={toggleMenu}
      title={t("menu")}
    />
  );

  const btnSearchHelp = (
    <IconButton
      type="button"
      icon={EgwWebFont.question}
      active={showSearchHelp}
      className={styles["header__btn-help"]}
      onClick={toggleSearchHelp}
      title={t("help")}
    />
  );

  const btnFilters = (
    <IconButton
      className={styles["header__btn-filter"]}
      type="button"
      icon={EgwWebFont.filter}
      active={showAdvSearchPanel}
      onClick={() => dispatch(systemActions.setShowAdvSearchPanel(!showAdvSearchPanel))}
      title={t("filter")}
    />
  );

  const btnSearch = (
    <IconButton
      icon={EgwWebFont.search}
      type="button"
      className={styles["header__btn-filter__mobile"]}
      onClick={() => setShowMobileForm(true)}
      title={t("search")}
    />
  );

  const btnPlay = <PlayControl/>;

  const btnFullscreen = isMobileAndHydrated ? null : (
    <IconButton
      icon={fullScreen ? EgwWebFont["fullscreen-exit"] : EgwWebFont["fullscreen"]}
      className={styles["header__btn-fullscreen"]}
      active={fullScreen}
      title={fullScreen ? t("minimize") : t("fullscreen")}
      onClick={toggleFullScreen}
    />
  );

  return (
    <header
      className={classNames(styles["header"], {
        [styles["form-focused"]]: isShowMobileForm,
      })}>
      <Container className={styles["header__content-wrapper"]}>
        {
          isShowMobileForm && (
            <IconButton
              className={styles["header__btn-back"]}
              icon={EgwWebFont["chevron-left"]}
              onClick={hideMobileForm}
            />
          )
        }

        {isShowMobileForm ? null : btnMenu}

        {
          !isShowMobileForm && (
            <Logotype
              className={styles["header__logo"]}
              writingsTextClassName={styles["logo__text-writings"]}
              onClick={() => {
                dispatch(systemActions.setShowAdvSearchPanel(false));
              }}
            />
          )
        }

        {(isShowMobileForm || !isMobileAndHydrated) && <HeaderForm
          initialValue={searchParams.queryFull ?? searchParams.query}
          className={styles["header-form"]}
          beforeInput={isMobileAndHydrated ? null : btnFilters}
          afterInput={isMobileAndHydrated ? null : btnSearchHelp}
          onSearch={search}
        />}

        {(!isShowMobileForm && isMobileAndHydrated) && (
          isReader ? (
            <>
              {btnPlay}
              {btnSearch}
            </>
          ) : (
            <>
              {btnSearch}
              {elemThemeDropDown}
            </>
          )
        )}

        {btnFullscreen}
        {!isMobileAndHydrated && elemThemeDropDown}

        {isShowMobileForm && isMobileAndHydrated ? (
          <ThreeDots
            className={styles["header__three-dots"]}
            classNamePopupContent={styles["three-dots_body"]}
            onShow={closeSearchHelp}
          >
            {
              ({ hide }) => (
                <>
                  <div className={styles["click-wrapper"]} onClick={() => {
                    hide();
                    hideMobileForm();
                  }}>
                    {btnMenu}
                    {btnFilters}
                    {btnSearchHelp}
                  </div>
                  {elemThemeDropDown}
                  <LinkButton href={"/"}>
                    <IconView icon={EgwWebFont.home}/>
                  </LinkButton>
                </>
              )
            }
          </ThreeDots>) : null}

        {
          !isMobileAndHydrated && (
            <LinkButton
              paddingLess
              title={t("goToFullApp")}
              className={styles["header__go-to-full-app"]}
              href={fullVersionLink}>
              <IconView icon={EgwWebFont["go-to-full-app"]}/>
            </LinkButton>
          )
        }

        {showSearchHelp && (
          <Portal id={EL_ID_PORTAL_MAIN_AFTER}>
            <div tabIndex={0}>
              <SearchExampleView
                className={styles["header__search-help"]}
                onClose={closeSearchHelp}
                onSearch={search}
              />
              <Backdrop
                className={styles["header__search-help__backdrop"]}
                onClick={closeSearchHelp}
              />
            </div>
          </Portal>
        )}
      </Container>

      <PanelHeaderMenu
        isOpen={showSideMenu}
        links={MenuItemsFinal}
        onClose={toggleMenu}
      />
    </header>
  );
};

Header.propTypes = {
  fullVersionLink: PropTypes.string,
};

export default Header;
